import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["stage", "wrap", "row", "thread", "mark"]
  static values = {
    stageWidth: { type: Number, default: 1200 },
    stageHeight: { type: Number, default: 640 },
    clearIn: { type: Number, default: 370 },
    hold: { type: Number, default: 3000 },
    fadeOut: { type: Number, default: 420 },
    minGap: { type: Number, default: 800 },
    maxGap: { type: Number, default: 1500 },
    firstDelay: { type: Number, default: 2300 },
  }

  connect() {
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.tracking = null
    this.rafId = null
    this.timers = []
    this.stopped = false

    this.setupStreams()

    const lines = this.threadTarget.querySelectorAll("line")
    const circles = this.threadTarget.querySelectorAll("circle")
    this.glowLine = lines[0]
    this.coreLine = lines[1]
    this.dotM = circles[0]
    this.dotC = circles[1]

    this.onResize = () => this.fit()
    window.addEventListener("resize", this.onResize)
    this.fit()

    this.onLoad = () => this.run()
    if (!this.reduced) {
      if (document.readyState === "complete") {
        this.run()
      } else {
        window.addEventListener("load", this.onLoad, { once: true })
      }
    }
  }

  disconnect() {
    this.stopped = true
    window.removeEventListener("load", this.onLoad)
    window.removeEventListener("resize", this.onResize)
    this.timers.forEach(clearTimeout)
    this.timers = []
    if (this.rafId) cancelAnimationFrame(this.rafId)
  }

  fit() {
    const vw = this.wrapTarget.clientWidth
    const s = Math.min(1, vw / this.stageWidthValue)
    this.stageW = vw / s
    this.stageCx = this.stageW / 2
    this.stageTarget.style.width = `${this.stageW}px`
    this.stageTarget.style.transform = `scale(${s})`
    this.wrapTarget.style.height = `${this.stageHeightValue * s}px`
    this.stageTarget.style.setProperty("--cm-label-scale", `${Math.min(2.2, Math.max(1, 0.65 / s))}`)
  }

  // Duplicate each track's cards in place so the marquee can loop seamlessly.
  setupStreams() {
    this.rowTargets.forEach((row) => {
      const track = row.querySelector(".landing-cm-track")
      Array.from(track.children).forEach((card) => track.appendChild(card.cloneNode(true)))
    })

    this.memberRows = this.rowTargets.filter((r) => r.dataset.landingCmSide === "member")
    this.oppRow = this.rowTargets.find((r) => r.dataset.landingCmSide === "opp")
  }

  stagePoint(el) {
    const sr = this.stageTarget.getBoundingClientRect()
    const sc = sr.width / this.stageW
    const r = el.getBoundingClientRect()
    return {
      x: (r.left + r.width / 2 - sr.left) / sc,
      y: (r.top + r.height / 2 - sr.top) / sc,
    }
  }

  clearOut() {
    return Math.min(this.stageCx - 100, 640)
  }

  inSideZone(x) {
    const d = Math.abs(x - this.stageCx)
    return d > this.clearInValue && d < this.clearOut()
  }

  sameSide(a, b) {
    return Math.sign(a - this.stageCx) === Math.sign(b - this.stageCx)
  }

  pickPair() {
    const mCands = []
    this.memberRows.forEach((row) =>
      row.querySelectorAll(".landing-cm-card").forEach((c) => {
        const p = this.stagePoint(c)
        if (this.inSideZone(p.x)) mCands.push({ el: c, p })
      }),
    )
    if (!mCands.length) return null

    const m = mCands[Math.floor(Math.random() * mCands.length)]
    let best = null
    let bestDx = 1e9
    this.oppRow.querySelectorAll(".landing-cm-card").forEach((c) => {
      const p = this.stagePoint(c)
      const dx = Math.abs(p.x - m.p.x)
      if (dx > 210 || !this.inSideZone(p.x) || !this.sameSide(p.x, m.p.x)) return
      const roleBonus = c.dataset.role === m.el.dataset.role ? 0 : 1000
      if (dx + roleBonus < bestDx) {
        bestDx = dx + roleBonus
        best = { el: c, p }
      }
    })
    if (!best) return null

    if (best.el.dataset.role !== m.el.dataset.role) {
      m.el.querySelector(".landing-cm-card__role").textContent = best.el.dataset.role
    }
    return { m: m.el, c: best.el }
  }

  trackThread() {
    if (!this.tracking) return
    const a = this.stagePoint(this.tracking.m)
    const b = this.stagePoint(this.tracking.c)
    if (Math.abs(a.x - b.x) > 600) {
      this.endMatch(this.tracking)
      return
    }
    ;[this.glowLine, this.coreLine].forEach((l) => {
      l.setAttribute("x1", a.x)
      l.setAttribute("y1", a.y)
      l.setAttribute("x2", b.x)
      l.setAttribute("y2", b.y)
    })
    this.dotM.setAttribute("cx", a.x)
    this.dotM.setAttribute("cy", a.y)
    this.dotC.setAttribute("cx", b.x)
    this.dotC.setAttribute("cy", b.y)
    this.rafId = requestAnimationFrame(() => this.trackThread())
  }

  endMatch(pair) {
    this.tracking = null
    this.threadTarget.classList.remove("landing-cm-thread--on")
    this.markTarget.classList.remove("landing-cm-mark--lit")
    pair.m.classList.remove("landing-cm-card--lit")
    pair.c.classList.remove("landing-cm-card--lit")
  }

  wait(ms) {
    return new Promise((resolve) => this.timers.push(setTimeout(resolve, ms)))
  }

  async run() {
    await this.wait(this.firstDelayValue)
    while (!this.stopped) {
      while (document.hidden && !this.stopped) await this.wait(500)
      if (this.stopped) return

      const pair = this.pickPair()
      if (!pair) {
        await this.wait(800)
        continue
      }

      const tracking = { m: pair.m, c: pair.c }
      pair.m.classList.add("landing-cm-card--lit")
      pair.c.classList.add("landing-cm-card--lit")
      this.tracking = tracking
      this.rafId = requestAnimationFrame(() => this.trackThread())
      this.threadTarget.classList.add("landing-cm-thread--on")
      this.markTarget.classList.add("landing-cm-mark--lit")

      await this.wait(this.holdValue)
      if (this.tracking) this.endMatch(tracking)
      await this.wait(this.fadeOutValue + this.minGapValue + Math.random() * (this.maxGapValue - this.minGapValue))
    }
  }
}
